import { ContextSource, Signal, SignalCategory } from '../types/context.types';

interface BuildDynamicGreetingInput {
  templateTitle: string;
  readinessScore: number;
  signals: Signal[];
  sources: ContextSource[];
  // Optional override: if provided, replaces the static per-template question
  // list. Use this with backend-generated, project-specific questions.
  dynamicQuestions?: string[];
}

const firstLabel = (signals: Signal[], category: SignalCategory): string | undefined =>
  signals.find((s) => s.isActive && s.category === category)?.label;

const allLabels = (signals: Signal[], category: SignalCategory): string[] =>
  signals.filter((s) => s.isActive && s.category === category).map((s) => s.label);

type IntroVariant = 'audienceAndGoal' | 'audienceOnly' | 'goalOnly' | 'sourcesOnly';

type FollowUp = {
  text: string;
  // If the project already has at least one active signal in this category,
  // skip the question — we already know the answer from onboarding.
  skipIfHas?: SignalCategory;
};

type TemplateQuestionSet = {
  intro: Record<IntroVariant, string>;
  questions: FollowUp[];
};

// Per-template intro + follow-up questions. Keyed by lowercased template title
// so we match the Firestore-seeded titles regardless of casing drift.
const TEMPLATE_QUESTIONS: Record<string, TemplateQuestionSet> = {
  wireframes: {
    intro: {
      audienceAndGoal: "I'll sketch wireframes that serve those signals.",
      audienceOnly: "I'll sketch wireframes for that audience.",
      goalOnly: "I'll wireframe toward that goal.",
      sourcesOnly: "I'll ground the wireframes in what you uploaded.",
    },
    questions: [
      { text: 'Which screen or flow should we start with?' },
      { text: "Who's the primary user we're designing for?", skipIfHas: 'audience' },
      { text: 'Any must-include elements or sections?' },
      {
        text: 'Any platform, brand, or accessibility constraints to respect?',
        skipIfHas: 'constraint',
      },
    ],
  },
  'competitive analysis': {
    intro: {
      audienceAndGoal: "I'll frame the competitive landscape against those signals.",
      audienceOnly: "I'll frame competitors through that audience's lens.",
      goalOnly: "I'll frame competitors against that goal.",
      sourcesOnly: "I'll work from what you uploaded.",
    },
    questions: [
      { text: 'Which competitors should we look at first?' },
      { text: 'What product or service are we positioning?' },
      { text: 'Which dimensions matter most (features, pricing, positioning, UX)?' },
      {
        text: 'What strategic question are we trying to answer?',
        skipIfHas: 'goal',
      },
    ],
  },
  personas: {
    intro: {
      audienceAndGoal: "I'll draft personas grounded in that audience and goal.",
      audienceOnly: "I'll draft personas grounded in that audience.",
      goalOnly: "I'll draft personas oriented to that goal.",
      sourcesOnly: "I'll pull personas from what you uploaded.",
    },
    questions: [
      { text: 'How many personas do you want?' },
      { text: 'Any specific roles, behaviors, or contexts to anchor on?' },
      { text: 'What research are we drawing from beyond what is loaded?' },
      { text: "Who's the primary audience to lead with?", skipIfHas: 'audience' },
    ],
  },
  'sitemaps / ia': {
    intro: {
      audienceAndGoal: "I'll structure the IA around that audience and goal.",
      audienceOnly: "I'll structure the IA around that audience.",
      goalOnly: "I'll structure the IA toward that goal.",
      sourcesOnly: "I'll structure the IA from what you uploaded.",
    },
    questions: [
      { text: 'Which entry point or domain are we structuring?' },
      { text: 'How many top-level sections do you have in mind?' },
      { text: 'Any existing IA or content inventory to start from?' },
      { text: "Who's the primary user navigating this?", skipIfHas: 'audience' },
    ],
  },
  'sitemaps/ia': {
    intro: {
      audienceAndGoal: "I'll structure the IA around that audience and goal.",
      audienceOnly: "I'll structure the IA around that audience.",
      goalOnly: "I'll structure the IA toward that goal.",
      sourcesOnly: "I'll structure the IA from what you uploaded.",
    },
    questions: [
      { text: 'Which entry point or domain are we structuring?' },
      { text: 'How many top-level sections do you have in mind?' },
      { text: 'Any existing IA or content inventory to start from?' },
      { text: "Who's the primary user navigating this?", skipIfHas: 'audience' },
    ],
  },
  'accessibility compliance': {
    intro: {
      audienceAndGoal: "I'll review against WCAG with those signals in mind.",
      audienceOnly: "I'll review against WCAG with that audience in mind.",
      goalOnly: "I'll review against WCAG toward that goal.",
      sourcesOnly: "I'll review what you uploaded against WCAG.",
    },
    questions: [
      { text: 'Which surface or page are we auditing first?' },
      { text: 'Which WCAG version and level (2.1 AA, 2.2 AA, etc.)?' },
      { text: 'Any assistive tech to prioritize (screen readers, keyboard, voice)?' },
      { text: 'Any known issues to focus on first?' },
    ],
  },
  'summarize transcripts': {
    intro: {
      audienceAndGoal: "I'll pull the signal out of the transcripts toward those signals.",
      audienceOnly: "I'll pull the signal that matters to that audience.",
      goalOnly: "I'll pull the signal toward that goal.",
      sourcesOnly: "I'll work through what you uploaded.",
    },
    questions: [
      { text: 'Paste or upload the transcripts when ready.' },
      { text: 'What format do you want — themes, decisions, action items, all of the above?' },
      { text: 'How long do they total — minutes, hours?' },
      { text: "Who's reading the summary?", skipIfHas: 'audience' },
    ],
  },
  'summarize notes': {
    intro: {
      audienceAndGoal: "I'll synthesize the notes against those signals.",
      audienceOnly: "I'll synthesize the notes for that audience.",
      goalOnly: "I'll synthesize the notes toward that goal.",
      sourcesOnly: "I'll work from the notes you uploaded.",
    },
    questions: [
      { text: 'Paste or upload the notes when ready.' },
      { text: 'What output do you want — TL;DR, bullets, structured doc?' },
      { text: 'Any specific themes to surface?' },
      { text: "Who's reading the summary?", skipIfHas: 'audience' },
    ],
  },
  'summarize meetings': {
    intro: {
      audienceAndGoal: "I'll pull decisions and actions toward those signals.",
      audienceOnly: "I'll pull decisions and actions relevant to that audience.",
      goalOnly: "I'll pull decisions and actions toward that goal.",
      sourcesOnly: "I'll work from the meeting notes you uploaded.",
    },
    questions: [
      { text: 'Paste or upload the meeting notes when ready.' },
      { text: 'Decisions, action items, or both?' },
      { text: 'Anything specific to flag — blockers, risks, owners?' },
      { text: "Who's reading the recap?", skipIfHas: 'audience' },
    ],
  },
  'research synthesis': {
    intro: {
      audienceAndGoal: "I'll synthesize findings against those signals.",
      audienceOnly: "I'll synthesize findings for that audience.",
      goalOnly: "I'll synthesize findings toward that goal.",
      sourcesOnly: "I'll synthesize what you uploaded.",
    },
    questions: [
      { text: 'What research are we synthesizing — interviews, surveys, usability tests?' },
      { text: 'How many participants or sources?' },
      { text: 'Any themes you have already noticed?' },
      { text: "What's the framing question?", skipIfHas: 'goal' },
    ],
  },
  'user flows': {
    intro: {
      audienceAndGoal: "I'll map flows for that audience toward that goal.",
      audienceOnly: "I'll map flows for that audience.",
      goalOnly: "I'll map flows toward that goal.",
      sourcesOnly: "I'll ground the flows in what you uploaded.",
    },
    questions: [
      { text: 'Where does the journey start — entry point, trigger?' },
      { text: 'Happy path only, or edge cases too?' },
      { text: "What's the goal state at the end?", skipIfHas: 'goal' },
      { text: 'Which user are we mapping?', skipIfHas: 'audience' },
    ],
  },
  'ux roadmap': {
    intro: {
      audienceAndGoal: "I'll plot a roadmap against those signals.",
      audienceOnly: "I'll plot a roadmap for that audience.",
      goalOnly: "I'll plot a roadmap toward that goal.",
      sourcesOnly: "I'll plot a roadmap from what you uploaded.",
    },
    questions: [
      { text: "What's the time horizon — quarter, half, year?" },
      { text: 'Any fixed dates or milestones — launches, reviews?' },
      { text: 'Who needs to align on this — PM, eng, leadership?' },
      { text: 'What outcomes are we tracking?', skipIfHas: 'goal' },
    ],
  },
  'design reviews': {
    intro: {
      audienceAndGoal: "I'll critique against those signals.",
      audienceOnly: "I'll critique through that audience's lens.",
      goalOnly: "I'll critique against that goal.",
      sourcesOnly: "I'll critique what you uploaded.",
    },
    questions: [
      { text: 'What are we reviewing — wireframes, mockups, a live product?' },
      { text: "Which heuristics or rubric — Nielsen's 10, your own, accessibility-first?" },
      { text: 'Any specific concerns to focus on first?' },
      { text: "Who's the design for?", skipIfHas: 'audience' },
    ],
  },
  'interaction design': {
    intro: {
      audienceAndGoal: "I'll design interactions for that audience toward that goal.",
      audienceOnly: "I'll design interactions for that audience.",
      goalOnly: "I'll design interactions toward that goal.",
      sourcesOnly: "I'll ground the interactions in what you uploaded.",
    },
    questions: [
      { text: 'Which interaction or component first?' },
      { text: "What's the trigger and the desired feedback?" },
      { text: 'Any platform conventions to honor — iOS, Android, web?' },
      { text: "Who's the user?", skipIfHas: 'audience' },
    ],
  },
  workshops: {
    intro: {
      audienceAndGoal: "I'll plan a workshop for that audience toward that goal.",
      audienceOnly: "I'll plan a workshop for that audience.",
      goalOnly: "I'll plan a workshop toward that goal.",
      sourcesOnly: "I'll plan a workshop grounded in what you uploaded.",
    },
    questions: [
      { text: 'How long do we have — 60 min, half day, full day?' },
      { text: "Who's in the room — roles, experience?" },
      { text: 'In-person, remote, or hybrid?' },
      { text: "What's the desired outcome?", skipIfHas: 'goal' },
    ],
  },
  ideation: {
    intro: {
      audienceAndGoal: "I'll generate ideas for that audience toward that goal.",
      audienceOnly: "I'll generate ideas for that audience.",
      goalOnly: "I'll generate ideas toward that goal.",
      sourcesOnly: "I'll generate ideas grounded in what you uploaded.",
    },
    questions: [
      { text: 'Topic or problem area?' },
      { text: 'Quantity bias (50 wild ideas) or quality bias (5 grounded ones)?' },
      { text: 'Any constraints to push against?', skipIfHas: 'constraint' },
      { text: "Who's the ideation for?", skipIfHas: 'audience' },
    ],
  },
};

const DEFAULT_QUESTIONS: TemplateQuestionSet = {
  intro: {
    audienceAndGoal: "I'll work from those signals.",
    audienceOnly: "I'll work for that audience.",
    goalOnly: "I'll work toward that goal.",
    sourcesOnly: "I'll work from what you uploaded.",
  },
  questions: [
    { text: 'What do you want first?' },
    { text: "Who's the audience?", skipIfHas: 'audience' },
    { text: 'Any constraints to keep in mind?', skipIfHas: 'constraint' },
  ],
};

const buildIntroAndQuestions = (
  templateTitle: string,
  variant: IntroVariant,
  knownCategories: Set<SignalCategory>
): { intro: string; questions: string[] } => {
  const set = TEMPLATE_QUESTIONS[templateTitle.toLowerCase()] ?? DEFAULT_QUESTIONS;
  const questions = set.questions
    .filter((q) => !q.skipIfHas || !knownCategories.has(q.skipIfHas))
    .map((q) => q.text);
  return { intro: set.intro[variant], questions };
};

export function buildDynamicGreeting({
  templateTitle,
  readinessScore,
  signals,
  sources,
  dynamicQuestions,
}: BuildDynamicGreetingInput): string | null {
  const activeSignals = signals.filter((s) => s.isActive);
  const readySourceCount = sources.filter((s) => s.status === 'ready').length;

  if (readinessScore === 0 || (activeSignals.length === 0 && readySourceCount === 0)) {
    return null;
  }

  const audience = firstLabel(activeSignals, 'audience');
  const goal = firstLabel(activeSignals, 'goal');
  const tone = firstLabel(activeSignals, 'tone');
  const constraints = allLabels(activeSignals, 'constraint');

  const docTail =
    readySourceCount === 0
      ? ''
      : readySourceCount === 1
        ? ', plus the document you uploaded'
        : `, plus the ${readySourceCount} documents you uploaded`;

  let opener: string;
  let variant: IntroVariant;
  if (audience && goal) {
    opener = `Working from your context — **${audience}** with the goal of **${goal}**${docTail}.`;
    variant = 'audienceAndGoal';
  } else if (audience) {
    opener = `Working from your context — **${audience}**${docTail}.`;
    variant = 'audienceOnly';
  } else if (goal) {
    opener = `Working from your context — goal of **${goal}**${docTail}.`;
    variant = 'goalOnly';
  } else if (readySourceCount > 0) {
    opener =
      readySourceCount === 1
        ? `Working from the document you uploaded.`
        : `Working from the ${readySourceCount} documents you uploaded.`;
    variant = 'sourcesOnly';
  } else {
    return null;
  }

  const knownCategories = new Set<SignalCategory>(activeSignals.map((s) => s.category));
  const { intro, questions: staticQuestions } = buildIntroAndQuestions(
    templateTitle,
    variant,
    knownCategories
  );

  // Prefer backend-generated, project-specific questions when available; fall
  // back to the static per-template list (used during the initial render
  // before the API call returns, or if it errors).
  const questions =
    dynamicQuestions && dynamicQuestions.length > 0 ? dynamicQuestions : staticQuestions;

  const lines: string[] = [`${opener} ${intro}`, ...questions.map((q) => `- ${q}`)];

  if (constraints.length > 0) {
    lines.push(`Holding to: ${constraints.join(', ')}.`);
  }

  if (tone) {
    lines.push(`Keeping the tone **${tone}**.`);
  }

  return lines.join('\n');
}
